// copied from @fs/zion-router but has a custom redirect URL for login
import React, { lazy, Suspense, useEffect, useState } from 'react'
import { Route } from 'react-router-dom'
import { appPath } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import { useQueryParams } from '@fs/zion-router'
import { showTemple, usePermission } from '@fs/zion-permissions'
import { useOverlay } from '@fs/zion-ui'
import { publicAccountScreens } from '../lib/helpers'
import { signInUrl } from '../lib/authUrls'
import { useNavigate } from '../hooks'
import PageSpinner from './common/PageSpinner'

const ShowTempleOptionsOverlay = lazy(() => import('./ShowTempleOptionsOverlay'))

function CustomAuthRouteWrapper({ children, signedIn, userLoading }) {
  const { query } = useQueryParams()
  const [isMember, status] = usePermission('FtMemberUiPermission')
  const [fetchingTemplePermission, setFetchingTemplePermission] = useState(true)
  const showTempleOptionsOverlay = useOverlay()
  const { location, onPush, onReplace } = useNavigate()

  // The sole purpose of this URL is to add church membership data to a public FS account, effectively to make it a member FS account.
  const templeRedirectUrl = `/identity/settings/temple-experience?returnUrl=${encodeURIComponent(appPath)}`

  // If the user is not signed in, send them to the sign-in page.
  useEffect(() => {
    if (!signedIn && !userLoading) {
      onReplace(signInUrl(!!query?.memberUuid), undefined, true, true)
    }
  })

  // Redirect user to an identity page for them to enter their church record information to convert their public FS account to a member FS account.
  useEffect(() => {
    const getTemplePermission = async () => {
      const timer = setTimeout(() => setFetchingTemplePermission(false), 5000)
      const shouldShowTemple = await showTemple()
      if (!shouldShowTemple) showTempleOptionsOverlay.open()
      setFetchingTemplePermission(false)
      clearTimeout(timer)
    }
    // Don't check temple permissions on the account review step because this is the step where the patron can transition their public account to a member account and we don't want them redirected to the identity settings page
    // NOTE: We don't want to add this other path to `publicAccountScreens` because it is used elsewhere for other reasons where /confirmed should not be a publicAccountScreen
    if ([...publicAccountScreens, '/account/confirmed'].some((path) => location.pathname.includes(path))) {
      setFetchingTemplePermission(false)
    } else if (signedIn && !userLoading && status === 'loaded') {
      if (isMember) getTemplePermission()
      else onPush(templeRedirectUrl, undefined, false, true)
    }
  }, [isMember, location.pathname, onPush, showTempleOptionsOverlay, signedIn, status, templeRedirectUrl, userLoading])

  if (showTempleOptionsOverlay.isOpen) {
    return (
      <Suspense fallback="">
        <ShowTempleOptionsOverlay overlay={showTempleOptionsOverlay} />
      </Suspense>
    )
  }
  if (userLoading || fetchingTemplePermission) return <PageSpinner />
  if (signedIn) return children
  return <PageSpinner />
}

export default function CustomAuthRoute({ component: Component, children, ...rest }) {
  const userInfo = useUser()

  // This handles bishop/LCR sessions to automagically log in with OAuth for their current Church Account session
  return (
    <Route {...rest}>
      {(props) => <CustomAuthRouteWrapper {...userInfo}>{children || <Component {...props} />}</CustomAuthRouteWrapper>}
    </Route>
  )
}
